import { relations } from 'drizzle-orm'
import {
  boolean,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from 'drizzle-orm/pg-core'
import type { EvaluationStatus, EvaluatorType } from '../../constants/skill-check'
import type { FollowUpStatus } from '../../constants/training-enums'
import { users } from '../schema'
import { tenants } from './tenants'
import { trainingEnrollments } from './training-enrollment'

// Training Follow-ups（受講1件につき、テナント設定の回数分）
export const trainingFollowUps = pgTable(
  'training_follow_ups',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    enrollmentId: uuid('enrollment_id')
      .notNull()
      .references(() => trainingEnrollments.id, { onDelete: 'cascade' }),
    phase: integer('phase').notNull(),
    label: text('label').notNull(),
    status: text('status').$type<FollowUpStatus>().notNull().default('pending'),
    availableFrom: timestamp('available_from').notNull(),
    dueDate: timestamp('due_date').notNull(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  (table) => ({
    enrollmentPhaseUnique: unique().on(table.enrollmentId, table.phase),
    enrollmentIdIdx: index('training_follow_ups_enrollment_id_idx').on(table.enrollmentId),
    tenantIdIdx: index('training_follow_ups_tenant_id_idx').on(table.tenantId),
    tenantStatusIdx: index('training_follow_ups_tenant_status_idx').on(
      table.tenantId,
      table.status,
    ),
  }),
)

// Follow-up Skill Items（受講完了時点の項目のスナップショット。全回で同じ項目を評価する）
export const followUpSkillItems = pgTable(
  'follow_up_skill_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    enrollmentId: uuid('enrollment_id')
      .notNull()
      .references(() => trainingEnrollments.id, { onDelete: 'cascade' }),
    code: text('code').notNull(),
    category: text('category').notNull(),
    label: text('label').notNull(),
    sortOrder: integer('sort_order').notNull(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  (table) => ({
    enrollmentCodeUnique: unique().on(table.enrollmentId, table.code),
    enrollmentIdIdx: index('follow_up_skill_items_enrollment_id_idx').on(table.enrollmentId),
    tenantIdIdx: index('follow_up_skill_items_tenant_id_idx').on(table.tenantId),
  }),
)

// Follow-up Evaluations（回ごとに本人・上司の各1件）
export const followUpEvaluations = pgTable(
  'follow_up_evaluations',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    followUpId: uuid('follow_up_id')
      .notNull()
      .references(() => trainingFollowUps.id, { onDelete: 'cascade' }),
    evaluatorType: text('evaluator_type').$type<EvaluatorType>().notNull(),
    // 評価時点の評価者（上司は異動しうるため、評価した本人を保持する）
    evaluatorUserId: uuid('evaluator_user_id')
      .notNull()
      .references(() => users.id),
    status: text('status').$type<EvaluationStatus>().notNull().default('submitted'),
    submittedAt: timestamp('submitted_at').defaultNow().notNull(),
    returnedAt: timestamp('returned_at'),
    returnedBy: uuid('returned_by').references(() => users.id),
    returnReason: text('return_reason'),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  (table) => ({
    followUpEvaluatorUnique: unique().on(table.followUpId, table.evaluatorType),
    followUpIdIdx: index('follow_up_evaluations_follow_up_id_idx').on(table.followUpId),
    tenantIdIdx: index('follow_up_evaluations_tenant_id_idx').on(table.tenantId),
  }),
)

// Follow-up Evaluation Scores（項目コード単位の点数）
export const followUpEvaluationScores = pgTable(
  'follow_up_evaluation_scores',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    evaluationId: uuid('evaluation_id')
      .notNull()
      .references(() => followUpEvaluations.id, { onDelete: 'cascade' }),
    itemCode: text('item_code').notNull(),
    score: integer('score').notNull(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    evaluationItemUnique: unique().on(table.evaluationId, table.itemCode),
    evaluationIdIdx: index('follow_up_evaluation_scores_evaluation_id_idx').on(table.evaluationId),
  }),
)

// Follow-up Supervisor Assignments（人事による上司評価者の個別指定。無ければ評価時点の組織マスター上の上司が評価する）
export const followUpSupervisorAssignments = pgTable(
  'follow_up_supervisor_assignments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    enrollmentId: uuid('enrollment_id')
      .notNull()
      .unique()
      .references(() => trainingEnrollments.id, { onDelete: 'cascade' }),
    supervisorUserId: uuid('supervisor_user_id')
      .notNull()
      .references(() => users.id),
    assignedBy: uuid('assigned_by')
      .notNull()
      .references(() => users.id),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  (table) => ({
    tenantIdIdx: index('follow_up_supervisor_assignments_tenant_id_idx').on(table.tenantId),
  }),
)

// Follow-up Comments（任意。回の完了判定には使わない）
export const followUpComments = pgTable(
  'follow_up_comments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    followUpId: uuid('follow_up_id')
      .notNull()
      .references(() => trainingFollowUps.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id),
    content: text('content').notNull(),
    isSupervisor: boolean('is_supervisor').notNull(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  (table) => ({
    followUpIdIdx: index('follow_up_comments_follow_up_id_idx').on(table.followUpId),
    tenantIdIdx: index('follow_up_comments_tenant_id_idx').on(table.tenantId),
  }),
)

// Relations
export const trainingFollowUpsRelations = relations(trainingFollowUps, ({ one, many }) => ({
  enrollment: one(trainingEnrollments, {
    fields: [trainingFollowUps.enrollmentId],
    references: [trainingEnrollments.id],
  }),
  followUpComments: many(followUpComments),
  followUpEvaluations: many(followUpEvaluations),
}))

export const followUpSkillItemsRelations = relations(followUpSkillItems, ({ one }) => ({
  enrollment: one(trainingEnrollments, {
    fields: [followUpSkillItems.enrollmentId],
    references: [trainingEnrollments.id],
  }),
}))

export const followUpEvaluationsRelations = relations(followUpEvaluations, ({ one, many }) => ({
  followUp: one(trainingFollowUps, {
    fields: [followUpEvaluations.followUpId],
    references: [trainingFollowUps.id],
  }),
  scores: many(followUpEvaluationScores),
}))

export const followUpEvaluationScoresRelations = relations(followUpEvaluationScores, ({ one }) => ({
  evaluation: one(followUpEvaluations, {
    fields: [followUpEvaluationScores.evaluationId],
    references: [followUpEvaluations.id],
  }),
}))

export const followUpCommentsRelations = relations(followUpComments, ({ one }) => ({
  followUp: one(trainingFollowUps, {
    fields: [followUpComments.followUpId],
    references: [trainingFollowUps.id],
  }),
  user: one(users, {
    fields: [followUpComments.userId],
    references: [users.id],
  }),
}))

// Type exports
export type TrainingFollowUp = typeof trainingFollowUps.$inferSelect
export type NewTrainingFollowUp = typeof trainingFollowUps.$inferInsert

export type FollowUpSkillItem = typeof followUpSkillItems.$inferSelect
export type NewFollowUpSkillItem = typeof followUpSkillItems.$inferInsert

export type FollowUpEvaluation = typeof followUpEvaluations.$inferSelect
export type NewFollowUpEvaluation = typeof followUpEvaluations.$inferInsert

export type FollowUpEvaluationScore = typeof followUpEvaluationScores.$inferSelect
export type NewFollowUpEvaluationScore = typeof followUpEvaluationScores.$inferInsert

export type FollowUpSupervisorAssignment = typeof followUpSupervisorAssignments.$inferSelect

export type FollowUpComment = typeof followUpComments.$inferSelect
export type NewFollowUpComment = typeof followUpComments.$inferInsert
